import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { AchExceptionService, CheckExceptionService } from '@treasury/domain/arp';
import { EntitlementsService } from '@treasury/domain/entitlements';
import { TmBaseComponent } from '@treasury/presentation';
import { achIcon, checkRangeIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-card';
import '@treasury/presentation/components/tm-widget-button';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export const tagName = 'positive-pay-decision-widget';
@customElement(tagName)
export class PositivePayDecisionWidget extends TmBaseComponent {
    @property({ type: Boolean })
    public disabled = false;

    @InjectProperty()
    declare private entitlementsService: EntitlementsService;

    @InjectProperty()
    declare private checkExceptionsServices: CheckExceptionService;

    @InjectProperty()
    declare private achExceptionService: AchExceptionService;

    @state()
    private entitlements = new Map<string, boolean>();

    @state()
    private loadingCheckExceptions = false;

    @state()
    private loadingAchExceptions = false;

    @state()
    private arpExceptionAccounts = [];

    @state()
    private checkExceptionCount = 0;

    @state()
    private achExceptionCount = 0;

    @InjectProperty()
    declare private readonly navService: NavigationService;

    @state()
    private hasAchExceptionEntitlement = false;

    @state()
    private hasCheckExceptionEntitlement = false;

    private get loading() {
        return this.loadingCheckExceptions || this.loadingAchExceptions;
    }

    protected firstUpdated() {
        this.loadEntitlements();
        this.loadAchExceptions();
        this.loadCheckExceptions();
    }

    protected render() {
        if (this.hasAchExceptionEntitlement || this.hasCheckExceptionEntitlement) {
            return html`
                <tm-card .header=${'Positive Pay Decisions'}>
                    ${this.renderPendingDecisionCounts()}
                </tm-card>
            `;
        }
        return nothing;
    }

    private async loadEntitlements() {
        this.entitlements = await this.entitlementsService.getEntitlements();
        this.hasAchExceptionEntitlement = this.entitlements.get('Work ACH Exceptions') || false;
        this.hasCheckExceptionEntitlement = this.entitlements.get('Work Check Exceptions') || false;
    }

    private async loadCheckExceptions() {
        try {
            this.loadingCheckExceptions = true;
            const checkExceptions = await this.checkExceptionsServices.getCheckExceptions();
            this.checkExceptionCount = checkExceptions.filter(
                exception => !exception.protected && !exception.decidedToday
            ).length;
        } catch {
            throw new Error('Error fetching check exceptions');
        } finally {
            this.loadingCheckExceptions = false;
        }
    }

    private async loadAchExceptions() {
        try {
            this.loadingAchExceptions = true;
            const achExceptions = await this.achExceptionService.getAchExceptions();
            this.achExceptionCount = achExceptions?.length ? achExceptions.length : 0;
        } catch {
            throw new Error('Error fetching ACH exceptions');
        } finally {
            this.loadingAchExceptions = false;
        }
    }

    private routeToDecisions(route: string) {
        this.navService.navigate(`/positive-pay/${route}`);
    }

    private handleKeyUp(event: KeyboardEvent, route: string) {
        const { key } = event;
        if (key === 'Return') {
            this.routeToDecisions(route);
        }
    }

    private renderAchExceptions() {
        if (!this.hasAchExceptionEntitlement) {
            return nothing;
        }
        return html`<tm-widget-button
            .label=${'ACH'}
            .icon=${achIcon}
            .action=${() => this.routeToDecisions('ach-exceptions')}
            .keyup=${(event: KeyboardEvent) => this.handleKeyUp(event, 'ach-exceptions')}
            .approvals=${this.achExceptionCount}
            .disabled=${false}
        ></tm-widget-button>`;
    }

    private renderCheckExceptions() {
        if (!this.hasCheckExceptionEntitlement) {
            return nothing;
        }
        return html`<tm-widget-button
            .label=${'Check'}
            .icon=${checkRangeIcon}
            .action=${() => this.routeToDecisions('check-exceptions')}
            .keyup=${(event: KeyboardEvent) => this.handleKeyUp(event, 'check-exceptions')}
            .approvals=${this.checkExceptionCount}
            .disabled=${this.disabled}
        ></tm-widget-button>`;
    }

    private renderPendingDecisionCounts() {
        if (this.loadingAchExceptions || this.loadingCheckExceptions) {
            return html`<tm-loader card></tm-loader>`;
        }
        if (!this.hasAchExceptionEntitlement && !this.hasCheckExceptionEntitlement) {
            return nothing;
        }
        return html`<div class="grid grid-auto-fill gap-4 justify-items-center px-4">
            ${this.renderAchExceptions()} ${this.renderCheckExceptions()}
        </div>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    --tm-card-min-width: auto;
                    --tm-card-min-height: 150px;
                    --icon-transform-scale: scale(1.6);
                }
                .grid-auto-fill {
                    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: PositivePayDecisionWidget;
    }
}
